import { useEffect, useState } from 'react';
import {
    checkPointInStage,
    convertToLayerPoint,
    getFramework,
    getViewportNode,
    useGlobalMask,
    useIdentity,
    useScale,
} from '@/framework';
import { IPoint } from '@/version-types';
import { useDrawEvent } from '../components/draw-pen-path';
import { EliminatePenController } from '../controller';
import { useEliminatePenState } from '../states/eliminate-pen-state';
import { ELIMINATE_PEN_EVENT, EliminateType, IDrawInfo, PenType } from '../types';
import { createPathInfoWithPointConvert, getExtendState } from '../utils';

const useDrawPen = () => {
    const scale = useScale();
    const eliminateConfig = useEliminatePenState()((state) => state.eliminateConfig);
    const { type, penType, penSize } = eliminateConfig;

    // 绘制时设置支持的画笔类型，手动消除笔只支持涂抹
    const drawPenType = type === EliminateType.LEGACY ? PenType.BRUSH : penType;
    // 绘制时候的画笔大小
    const drawPenSize = drawPenType === PenType.BRUSH ? penSize / scale : 32;
    // 画笔光标大小
    const penCursorSize = drawPenType === PenType.BRUSH ? drawPenSize : drawPenSize / scale;

    return {
        drawPenType,
        drawPenSize,
        penCursorSize,
    };
};

const useDrawInfo = () => {
    const identity = useIdentity();
    const framework = getFramework(identity);

    const originLayer = useEliminatePenState()((state) => state.originLayer);
    const setEliminateConfig = useEliminatePenState()((state) => state.setEliminateConfig);

    // 已经绘制完成的所有路径
    const [drawInfos, setDrawInfos] = useState<IDrawInfo[]>([]);
    // 当前正在绘制的路径
    const [drawingInfo, setDrawingInfo] = useState<IDrawInfo | null>(null);

    useEffect(() => {
        const handler = framework.events.onLayerChange(() => {
            if (!originLayer?.id) return;

            const layer = framework.getLayer(originLayer.id);

            if (!layer) return;

            const pathInfos = getExtendState(layer);

            console.log('===>pathInfos', pathInfos);
            setDrawInfos(pathInfos);
        });

        const historyHandler = framework.history.onBeforeChange(({ data, isRedo, isUndo }) => {
            if (!data || !(isRedo || isUndo)) return;
            if (!originLayer?.id) return;

            const { newLayer } = data;

            if (!newLayer) return;

            const pathInfos = getExtendState(newLayer);

            console.log('===>historyHandler', pathInfos);
            if (pathInfos.length) {
                setEliminateConfig({
                    type: EliminateType.AI,
                });
            }
        });

        return () => {
            framework.events.off(handler);
            framework.events.off(historyHandler);
        };
    }, [originLayer?.id]);

    return {
        drawInfos,
        drawingInfo,
        setDrawingInfo,
    };
};

export const useEliminateDraw = () => {
    const identity = useIdentity();
    const framework = getFramework(identity);
    const stageIndex = framework.stage.getActiveStageIndex();

    const globalMask = useGlobalMask();
    const isShowLoading = globalMask !== null;

    const eliminateConfig = useEliminatePenState()((state) => state.eliminateConfig);
    const originLayer = useEliminatePenState()((state) => state.originLayer);
    const setEliminateConfig = useEliminatePenState()((state) => state.setEliminateConfig);
    const controller = framework.getInstance(EliminatePenController);

    const {
        type,
        manualApply,
        selectionType,
        watermarkMaskURL,
        textMaskURL,
        autoRecognizeWatermark,
        autoRecognizeText,
        isWatermarkMaskUsed,
        isTextMaskUsed,
    } = eliminateConfig;

    // 记录画笔指针位置
    const [mousePosition, setMousePosition] = useState<IPoint>({ x: -10000, y: 0 });
    const { drawInfos, drawingInfo, setDrawingInfo } = useDrawInfo();

    const { drawPenType, drawPenSize, penCursorSize } = useDrawPen();

    const handleDrawEnd = async (points: IPoint[]) => {
        if (!originLayer) return;

        if (points.length) {
            const drawPathList = [
                ...drawInfos,
                {
                    penType: drawPenType,
                    penSize: drawPenSize,
                    selectionType,
                    points,
                },
            ];

            framework.events.emit(ELIMINATE_PEN_EVENT.DRAW_END);

            // 立即消除不用写入选区记录
            if (!manualApply) {
                const pathInfos = createPathInfoWithPointConvert(originLayer, drawPathList, true);
                await controller.eliminate(pathInfos);
            } else {
                controller.updatePathInfos(drawPathList);
            }
        }
        setDrawingInfo(null);
    };

    const updateDrawingInfo = (points: IPoint[]) => {
        if (points.length) {
            setDrawingInfo({
                penType: drawPenType,
                penSize: drawPenSize,
                selectionType,
                points,
            });
        }
    };

    const checkIsDrawInStage = (e) => {
        const point = { x: e.x, y: e.y };
        const viewportNode = getViewportNode(identity);

        // ATTENTION: 这里是为了解决出现登录弹窗、订阅弹窗时，在弹窗上点击也会触发绘制的问题
        if (!viewportNode?.contains(e.target as HTMLElement)) {
            return false;
        }

        return checkPointInStage(identity, point, stageIndex);
    };

    useDrawEvent({
        drawPenType,
        disable: isShowLoading,
        // ATTENTION: 这里转换出来的坐标是相对图层的坐标，但矩形选区的绘制需要计算相对视窗的四个点，因此不能在这里进行转换
        // 否则当图片带有旋转时，画出来的矩形也是旋转后的
        convertToLayerPoint: (point) => convertToLayerPoint(identity, point, stageIndex),
        isDrawEvent: checkIsDrawInStage,
        onMouseMove: (position) => setMousePosition(position),
        onDrawStart: updateDrawingInfo,
        onDrawMove: updateDrawingInfo,
        onDrawEnd: handleDrawEnd,
    });

    useEffect(() => {
        // 从 AI 消除切换到手动消除时，清空未应用的选区
        if (type === EliminateType.LEGACY && drawInfos.length > 0) {
            controller.updateLayerPathInfos([], true);
        }
    }, [type]);

    // 非即涂即消模式下，开始消除按钮控制
    useEffect(() => {
        if (manualApply) {
            const hasRecognize =
                (autoRecognizeWatermark && !!watermarkMaskURL && !isWatermarkMaskUsed) ||
                (autoRecognizeText && !!textMaskURL && !isTextMaskUsed);

            setEliminateConfig({ canApply: drawInfos.length > 0 || hasRecognize });
        }
    }, [
        drawInfos.length,
        manualApply,
        watermarkMaskURL,
        textMaskURL,
        autoRecognizeWatermark,
        autoRecognizeText,
        isWatermarkMaskUsed,
        isTextMaskUsed,
    ]);

    return {
        drawPenType,
        penCursorSize,
        penVisible: !isShowLoading,
        mousePosition,
        drawingPath: drawingInfo && originLayer ? createPathInfoWithPointConvert(originLayer, [drawingInfo])[0] : null,
        drawPathList: originLayer ? createPathInfoWithPointConvert(originLayer, drawInfos, true) : [],
    };
};
